import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import API from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowRight, ArrowLeft, Home, MapPin, DollarSign, Camera, CheckCircle, Upload, X, ImageIcon } from 'lucide-react';

const STEPS = ['Property Address', 'Property Details', 'Photos', 'Financials & Agent'];
const PROPERTY_TYPES = [
    { id: 'single_family', label: 'Single Family' }, { id: 'townhome', label: 'Townhome' },
    { id: 'condo', label: 'Condo' }, { id: 'multi_family', label: 'Multi-Family' },
    { id: 'duplex', label: 'Duplex' }, { id: 'mobile_home', label: 'Mobile Home' },
];
const CONDITIONS = [
    { id: 'excellent', label: 'Excellent', desc: 'Move-in ready, updated' },
    { id: 'good', label: 'Good', desc: 'Well maintained, minor cosmetic' },
    { id: 'fair', label: 'Fair', desc: 'Livable, needs updates' },
    { id: 'needs_work', label: 'Needs Work', desc: 'Requires renovation' },
];
const MOTIVATIONS = [
    { id: 'relocating', label: 'Relocating' }, { id: 'financial', label: 'Financial hardship' },
    { id: 'inherited', label: 'Inherited property' }, { id: 'vacant', label: 'Vacant / not rented' },
    { id: 'tired_landlord', label: 'Tired landlord' }, { id: 'divorce', label: 'Divorce' },
    { id: 'downsizing', label: 'Downsizing' }, { id: 'other', label: 'Other' },
];

export default function SellerIntakePage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [images, setImages] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [form, setForm] = useState({
        property_address: '', city: '', state: '', zip_code: '',
        property_type: 'single_family', bedrooms: 3, bathrooms: 2, sqft: '',
        lot_size: '', year_built: '', condition: 'good',
        description: '', features: '', parking: '',
        hoa_monthly: '', owner_finance_terms: '',
        estimated_value: '', remaining_mortgage: '',
        monthly_holding_costs: '', asking_price: '',
        days_on_market: '', motivation_level: '',
        agent_name: '', agent_email: '', agent_phone: '', agent_brokerage: '',
    });

    const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
    const next = () => { if (step < STEPS.length - 1) setStep(step + 1); };
    const prev = () => { if (step > 0) setStep(step - 1); };

    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length + images.length > 10) {
            alert('Maximum 10 photos allowed');
            return;
        }
        setUploading(true);
        for (const file of files) {
            if (file.size > 10 * 1024 * 1024) continue;
            const reader = new FileReader();
            reader.onload = (ev) => {
                setImages(prev => [...prev, { id: Math.random().toString(36).slice(2), preview: ev.target.result, file }]);
            };
            reader.readAsDataURL(file);
        }
        setUploading(false);
        e.target.value = '';
    };

    const removeImage = (id) => setImages(prev => prev.filter(img => img.id !== id));

    const submit = async () => {
        setLoading(true);
        setError('');
        try {
            await API.post('/sellers/intake', {
                ...form,
                estimated_value: parseFloat(form.estimated_value) || 0,
                remaining_mortgage: parseFloat(form.remaining_mortgage) || 0,
                monthly_holding_costs: parseFloat(form.monthly_holding_costs) || 0,
                asking_price: parseFloat(form.asking_price) || 0,
                days_on_market: parseInt(form.days_on_market) || 0,
                sqft: parseInt(form.sqft) || 0,
                year_built: parseInt(form.year_built) || 0,
                bedrooms: parseInt(form.bedrooms) || 0,
                bathrooms: parseFloat(form.bathrooms) || 0,
                hoa_monthly: parseFloat(form.hoa_monthly) || 0,
            });
            // Upload images after listing is created
            for (const img of images) {
                if (img.file) {
                    const fd = new FormData();
                    fd.append('file', img.file);
                    await API.post('/sellers/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).catch(() => {});
                }
            }
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.detail || 'Submission failed');
        } finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen bg-[#0A0A0A]" data-testid="seller-intake-page">
            <Navbar variant="dark" />
            <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-24 pb-12">
                <h1 className="text-2xl font-medium text-white font-['Outfit'] mb-2">List Your Property</h1>
                <p className="text-sm text-zinc-500 mb-8">Provide property details to connect with qualified buyers.</p>

                {/* Step indicator */}
                <div className="flex items-center gap-2 mb-8">
                    {STEPS.map((s, i) => (
                        <div key={s} className="flex items-center">
                            <div className="flex items-center gap-1.5 cursor-pointer" onClick={() => setStep(i)}>
                                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${i < step ? 'bg-emerald-600 text-white' : i === step ? 'bg-emerald-600/20 text-emerald-400 ring-2 ring-emerald-600' : 'bg-white/5 text-zinc-500'}`}>
                                    {i < step ? <CheckCircle className="w-3.5 h-3.5" /> : i + 1}
                                </div>
                                <span className="text-xs text-zinc-400 hidden sm:inline">{s}</span>
                            </div>
                            {i < STEPS.length - 1 && <div className={`w-8 sm:w-12 h-0.5 mx-2 ${i < step ? 'bg-emerald-600' : 'bg-white/10'}`} />}
                        </div>
                    ))}
                </div>

                {error && <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>}

                <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 sm:p-8">
                    {/* Step 0: Address */}
                    {step === 0 && (
                        <div className="space-y-4" data-testid="seller-step-address">
                            <div className="flex items-center gap-2 mb-4"><MapPin className="w-5 h-5 text-emerald-400" /><h2 className="text-lg font-medium text-white font-['Outfit']">Property Location</h2></div>
                            <div>
                                <Label className="text-zinc-400 text-sm">Street Address</Label>
                                <Input value={form.property_address} onChange={e => set('property_address', e.target.value)} data-testid="seller-address"
                                    placeholder="123 Main Street" className="bg-white/5 border-white/10 text-white mt-1" />
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <Label className="text-zinc-400 text-sm">City</Label>
                                    <Input value={form.city} onChange={e => set('city', e.target.value)} data-testid="seller-city"
                                        placeholder="Houston" className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-sm">State</Label>
                                    <Input value={form.state} onChange={e => set('state', e.target.value)} data-testid="seller-state"
                                        placeholder="TX" maxLength={2} className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-sm">ZIP Code</Label>
                                    <Input value={form.zip_code} onChange={e => set('zip_code', e.target.value)} data-testid="seller-zip"
                                        placeholder="77001" className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-zinc-400 text-sm">Days on Market</Label>
                                <Input type="number" value={form.days_on_market} onChange={e => set('days_on_market', e.target.value)} data-testid="seller-dom"
                                    placeholder="0" className="bg-white/5 border-white/10 text-white mt-1 max-w-[200px]" />
                                <p className="text-xs text-zinc-600 mt-1">Properties with 91+ days qualify for the Day 91 Marketplace.</p>
                            </div>
                        </div>
                    )}

                    {/* Step 1: Property Details */}
                    {step === 1 && (
                        <div className="space-y-4" data-testid="seller-step-details">
                            <div className="flex items-center gap-2 mb-4"><Home className="w-5 h-5 text-emerald-400" /><h2 className="text-lg font-medium text-white font-['Outfit']">Property Details</h2></div>
                            <div>
                                <Label className="text-zinc-400 text-sm">Property Type</Label>
                                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-1">
                                    {PROPERTY_TYPES.map(t => (
                                        <button key={t.id} onClick={() => set('property_type', t.id)}
                                            className={`text-xs px-2 py-2 rounded-lg border transition-all ${form.property_type === t.id ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' : 'border-white/10 text-zinc-400 hover:border-white/20'}`}>
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                <div>
                                    <Label className="text-zinc-400 text-sm">Bedrooms</Label>
                                    <Input type="number" value={form.bedrooms} onChange={e => set('bedrooms', e.target.value)} data-testid="seller-beds"
                                        className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-sm">Bathrooms</Label>
                                    <Input type="number" step="0.5" value={form.bathrooms} onChange={e => set('bathrooms', e.target.value)} data-testid="seller-baths"
                                        className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-sm">Square Feet</Label>
                                    <Input type="number" value={form.sqft} onChange={e => set('sqft', e.target.value)} data-testid="seller-sqft"
                                        placeholder="1800" className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-sm">Year Built</Label>
                                    <Input type="number" value={form.year_built} onChange={e => set('year_built', e.target.value)} data-testid="seller-year"
                                        placeholder="1995" className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-zinc-400 text-sm">Lot Size</Label>
                                    <Input value={form.lot_size} onChange={e => set('lot_size', e.target.value)}
                                        placeholder="e.g., 0.25 acres, 7,500 sqft" className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-sm">Parking</Label>
                                    <Input value={form.parking} onChange={e => set('parking', e.target.value)}
                                        placeholder="e.g., 2-car garage" className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-zinc-400 text-sm">Condition</Label>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
                                    {CONDITIONS.map(c => (
                                        <button key={c.id} onClick={() => set('condition', c.id)}
                                            className={`text-left px-3 py-2 rounded-lg border transition-all ${form.condition === c.id ? 'border-emerald-500 bg-emerald-500/10' : 'border-white/10 hover:border-white/20'}`}>
                                            <div className={`text-xs font-medium ${form.condition === c.id ? 'text-emerald-400' : 'text-zinc-300'}`}>{c.label}</div>
                                            <div className="text-[10px] text-zinc-500">{c.desc}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <Label className="text-zinc-400 text-sm">Property Description</Label>
                                <Textarea value={form.description} onChange={e => set('description', e.target.value)} data-testid="seller-desc"
                                    placeholder="Describe the property, recent upgrades, neighborhood highlights..."
                                    className="bg-white/5 border-white/10 text-white mt-1 min-h-20" />
                            </div>
                            <div>
                                <Label className="text-zinc-400 text-sm">Features (comma-separated)</Label>
                                <Input value={form.features} onChange={e => set('features', e.target.value)}
                                    placeholder="Pool, Fenced Yard, Updated Kitchen, Hardwood Floors" className="bg-white/5 border-white/10 text-white mt-1" />
                            </div>
                        </div>
                    )}

                    {/* Step 2: Photos */}
                    {step === 2 && (
                        <div className="space-y-4" data-testid="seller-step-photos">
                            <div className="flex items-center gap-2 mb-4"><Camera className="w-5 h-5 text-emerald-400" /><h2 className="text-lg font-medium text-white font-['Outfit']">Property Photos</h2></div>
                            <p className="text-sm text-zinc-500">Upload up to 10 photos. Good photos attract more qualified buyers.</p>

                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                {images.map(img => (
                                    <div key={img.id} className="relative aspect-[4/3] rounded-lg overflow-hidden border border-white/10 group">
                                        <img src={img.preview} alt="" className="w-full h-full object-cover" />
                                        <button onClick={() => removeImage(img.id)}
                                            className="absolute top-1 right-1 w-6 h-6 bg-black/70 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                            <X className="w-3 h-3 text-white" />
                                        </button>
                                    </div>
                                ))}
                                {images.length < 10 && (
                                    <label className="aspect-[4/3] rounded-lg border-2 border-dashed border-white/10 flex flex-col items-center justify-center cursor-pointer hover:border-emerald-500/50 transition-colors">
                                        <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" data-testid="seller-photo-input" />
                                        <Upload className="w-6 h-6 text-zinc-500 mb-1" />
                                        <span className="text-xs text-zinc-500">Add Photo</span>
                                        <span className="text-[10px] text-zinc-600 mt-0.5">{images.length}/10</span>
                                    </label>
                                )}
                            </div>
                            {images.length === 0 && (
                                <div className="text-center py-8 bg-white/5 rounded-xl border border-white/10">
                                    <ImageIcon className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
                                    <p className="text-sm text-zinc-500">No photos added yet</p>
                                    <p className="text-xs text-zinc-600 mt-1">You can also add photos later from your dashboard</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 3: Financials & Agent */}
                    {step === 3 && (
                        <div className="space-y-4" data-testid="seller-step-financials">
                            <div className="flex items-center gap-2 mb-4"><DollarSign className="w-5 h-5 text-emerald-400" /><h2 className="text-lg font-medium text-white font-['Outfit']">Financials & Agent Info</h2></div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-zinc-400 text-sm">Estimated Value ($)</Label>
                                    <Input type="number" value={form.estimated_value} onChange={e => set('estimated_value', e.target.value)} data-testid="seller-value"
                                        placeholder="250000" className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-sm">Asking Price ($)</Label>
                                    <Input type="number" value={form.asking_price} onChange={e => set('asking_price', e.target.value)} data-testid="seller-price"
                                        placeholder="240000" className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-sm">Remaining Mortgage ($)</Label>
                                    <Input type="number" value={form.remaining_mortgage} onChange={e => set('remaining_mortgage', e.target.value)} data-testid="seller-mortgage"
                                        placeholder="150000" className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-sm">Monthly Holding Costs ($)</Label>
                                    <Input type="number" value={form.monthly_holding_costs} onChange={e => set('monthly_holding_costs', e.target.value)} data-testid="seller-holding"
                                        placeholder="1800" className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-sm">HOA Monthly ($)</Label>
                                    <Input type="number" value={form.hoa_monthly} onChange={e => set('hoa_monthly', e.target.value)}
                                        placeholder="0" className="bg-white/5 border-white/10 text-white mt-1" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-zinc-400 text-sm">Motivation to Sell</Label>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
                                    {MOTIVATIONS.map(m => (
                                        <button key={m.id} onClick={() => set('motivation_level', m.id)}
                                            className={`text-xs px-2 py-2 rounded-lg border transition-all ${form.motivation_level === m.id ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' : 'border-white/10 text-zinc-400 hover:border-white/20'}`}>
                                            {m.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <Label className="text-zinc-400 text-sm">Owner Finance Terms (optional)</Label>
                                <Textarea value={form.owner_finance_terms} onChange={e => set('owner_finance_terms', e.target.value)}
                                    placeholder="e.g., 10% down, 8% interest, 30-year term, balloon at 5 years"
                                    className="bg-white/5 border-white/10 text-white mt-1 min-h-16" />
                            </div>
                            <div className="pt-4 border-t border-white/10">
                                <h3 className="text-sm font-medium text-zinc-300 mb-3">Listing Agent (optional)</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-zinc-400 text-sm">Agent Name</Label>
                                        <Input value={form.agent_name} onChange={e => set('agent_name', e.target.value)}
                                            className="bg-white/5 border-white/10 text-white mt-1" />
                                    </div>
                                    <div>
                                        <Label className="text-zinc-400 text-sm">Agent Email</Label>
                                        <Input value={form.agent_email} onChange={e => set('agent_email', e.target.value)}
                                            className="bg-white/5 border-white/10 text-white mt-1" />
                                    </div>
                                    <div>
                                        <Label className="text-zinc-400 text-sm">Agent Phone</Label>
                                        <Input value={form.agent_phone} onChange={e => set('agent_phone', e.target.value)}
                                            className="bg-white/5 border-white/10 text-white mt-1" />
                                    </div>
                                    <div>
                                        <Label className="text-zinc-400 text-sm">Brokerage</Label>
                                        <Input value={form.agent_brokerage} onChange={e => set('agent_brokerage', e.target.value)}
                                            className="bg-white/5 border-white/10 text-white mt-1" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Navigation */}
                    <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
                        <Button onClick={prev} disabled={step === 0} variant="ghost" className="text-zinc-400 hover:text-white">
                            <ArrowLeft className="w-4 h-4 mr-2" /> Back
                        </Button>
                        {step < STEPS.length - 1 ? (
                            <Button onClick={next} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg" data-testid="seller-next-btn">
                                Next <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        ) : (
                            <Button onClick={submit} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg" data-testid="seller-submit-btn">
                                {loading ? (uploading ? 'Uploading photos...' : 'Submitting...') : 'List Property'} <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
